import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import AddIcon from "@mui/icons-material/Add";

import SortableBulletList from "./asset-content/SortableBulletList";
import { BulletItem } from "./asset-content/BulletItemEditor";
import {
  AssetContent,
  AssetContentItem,
} from "../../../redux/reducers/assetReducer";

const ALLOWED_CONTENT_TYPES = ["story", "unknown", "faq_entry"] as const;
type ContentType = (typeof ALLOWED_CONTENT_TYPES)[number];

function itemsToMarkdown(items: AssetContentItem[]): string {
  return items
    .map((i) =>
      i.image_url ? `* ${i.text} [](${i.image_url})` : `* ${i.text}`
    )
    .join("\n");
}

export const ensureBulletIds = (bullets: AssetContentItem[]): BulletItem[] => {
  return bullets.map((bullet) => ({
    ...bullet,
    id:
      (bullet as BulletItem)?.id ||
      `bullet-${Date.now()}-${crypto.randomUUID()}`,
  }));
};

interface AssetContentEditorProps {
  content: AssetContent;
  index: number;
  disabled: boolean;
  onChange: (newContent: AssetContent) => void;
  onRemove?: () => void;
  onPreviewStory?: () => void;
  onViewMarkdown?: (markdown: string) => void;
}

const AssetContentEditor: React.FC<AssetContentEditorProps> = ({
  content,
  index,
  disabled,
  onChange,
  onRemove,
  onPreviewStory,
  onViewMarkdown,
}) => {
  const isStory = content.type === "story";
  const isFAQ = content.type === "faq_entry";

  const bulletItems: BulletItem[] = useMemo(() => {
    if (!disabled) {
      return ensureBulletIds(content.story_items || []);
    } else {
      return (content.story_items || []) as BulletItem[];
    }
  }, [content.story_items, disabled]);

  const handleSubjectChange = (newSubject: string) => {
    onChange({ ...content, subject: newSubject });
  };

  const handleNotesChange = (newNotes: string) => {
    onChange({ ...content, notes: newNotes });
  };

  const handleBulletsChange = (newBullets: AssetContentItem[]) => {
    onChange({ ...content, story_items: newBullets });
  };

  const handleRemoveBullet = (idx: number) => {
    const updated = [...(content.story_items || [])];
    updated.splice(idx, 1);
    handleBulletsChange(updated);
  };

  const handleAddBullet = () => {
    const newBullet: BulletItem = {
      id: `bullet-${Date.now()}-${crypto.randomUUID()}`,
      text: "",
      image_url: "",
    };
    handleBulletsChange([...(content.story_items || []), newBullet]);
  };

  const handleViewMarkdownClick = () => {
    if (onViewMarkdown) {
      const md = itemsToMarkdown(content.story_items || []);
      onViewMarkdown(md);
    }
  };

  // EVENT HANDLERS (FAQ)
  const handleFAQTopicChange = (newTopic: string) => {
    onChange({ ...content, faq_topic: newTopic });
  };

  const handleFAQEntryChange = (newEntry: string) => {
    onChange({ ...content, faq_entry: newEntry });
  };

  const handleSourceUrlChange = (newSourceUrl: string) => {
    onChange({ ...content, source_url: newSourceUrl });
  };

  // State for media field key-value pairs
  const [mediaKeyValuePairs, setMediaKeyValuePairs] = useState<
    Array<{ key: string; value: string }>
  >([]);

  // Initialize media key-value pairs from content
  useEffect(() => {
    if (content.faq_media && typeof content.faq_media === "object") {
      const pairs = Object.entries(content.faq_media).map(([key, value]) => ({
        key,
        value: String(value),
      }));
      setMediaKeyValuePairs(pairs);
    } else {
      setMediaKeyValuePairs([]);
    }
  }, [content.faq_media]);

  const handleAddMediaPair = () => {
    // Default to 'image_url' as the key
    setMediaKeyValuePairs([...mediaKeyValuePairs, { key: 'image_url', value: '' }]);
  };

  const handleRemoveMediaPair = (index: number) => {
    const newPairs = mediaKeyValuePairs.filter((_, i) => i !== index);
    setMediaKeyValuePairs(newPairs);
    updateMediaFromPairs(newPairs);
  };

  const handleMediaKeyChange = (index: number, newKey: string) => {
    // Allow the change but will show validation error if not 'image_url'
    const newPairs = [...mediaKeyValuePairs];
    newPairs[index].key = newKey;
    setMediaKeyValuePairs(newPairs);
    // Only update the actual media if the key is valid
    if (newKey === 'image_url' || newKey === '') {
      updateMediaFromPairs(newPairs);
    } else {
      // Clear the media to prevent saving with invalid key
      onChange({ ...content, faq_media: undefined });
    }
  };

  const handleMediaValueChange = (index: number, newValue: string) => {
    const newPairs = [...mediaKeyValuePairs];
    newPairs[index].value = newValue;
    setMediaKeyValuePairs(newPairs);
    updateMediaFromPairs(newPairs);
  };

  const updateMediaFromPairs = (
    pairs: Array<{ key: string; value: string }>
  ) => {
    // Filter out pairs with empty keys
    const validPairs = pairs.filter((pair) => pair.key.trim() !== "");

    if (validPairs.length === 0) {
      onChange({ ...content, faq_media: undefined });
    } else {
      const mediaObject: Record<string, string> = {};
      validPairs.forEach((pair) => {
        mediaObject[pair.key] = pair.value;
      });
      onChange({ ...content, faq_media: mediaObject });
    }
  };

  // EVENT HANDLER (content type)
  const handleTypeChange = (newType: ContentType) => {
    if (newType === "story") {
      onChange({
        ...content,
        type: "story",
        subject: content.subject || "",
        story_items: content.story_items || [],
        notes: content.notes || "",
        // Clear FAQ fields
        faq_topic: undefined,
        faq_entry: undefined,
        faq_media: undefined,
      });
    } else if (newType === "faq_entry") {
      onChange({
        ...content,
        type: "faq_entry",
        faq_topic: content.faq_topic || "",
        faq_entry: content.faq_entry || "",
        faq_media: content.faq_media || undefined,
        source_url: content.source_url || "",
        // Clear story fields
        subject: undefined,
        story_items: undefined,
        notes: undefined,
      });
    } else {
      // unknown
      onChange({
        ...content,
        type: "unknown",
        // Clear all type-specific fields
        subject: undefined,
        story_items: undefined,
        faq_topic: undefined,
        faq_entry: undefined,
        faq_media: undefined,
        notes: undefined,
      });
    }
  };

  return (
    <Box sx={{ p: 2, mb: 2 }}>
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1">Content #{index + 1}</Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {isStory && onPreviewStory && (
            <Tooltip title="Preview Story">
              <IconButton color="primary" size="small" onClick={onPreviewStory}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}

          {isStory && onViewMarkdown && (
            <Tooltip title="View Markdown">
              <IconButton
                color="primary"
                size="small"
                onClick={handleViewMarkdownClick}
                sx={{ ml: 1 }}
              >
                <DescriptionIcon />
              </IconButton>
            </Tooltip>
          )}

          {!disabled && onRemove && (
            <Tooltip title="Remove entire content block">
              <IconButton
                color="error"
                onClick={onRemove}
                size="small"
                sx={{ ml: 1 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        {/* Content Type */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Content Type
          </Typography>
          <Select
            value={content.type}
            size="small"
            disabled={disabled}
            onChange={(e) => handleTypeChange(e.target.value as ContentType)}
            sx={{ minWidth: 160 }}
          >
            {ALLOWED_CONTENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* STORY UI */}
      {isStory && (
        <>
          <TextField
            label="Story Subject"
            variant="outlined"
            size="small"
            fullWidth
            value={content.subject || ""}
            onChange={(e) => handleSubjectChange(e.target.value)}
            disabled={disabled}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Notes"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={6}
            value={content.notes || ""}
            onChange={(e) => handleNotesChange(e.target.value)}
            disabled={disabled}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Story Points
          </Typography>
          <SortableBulletList
            bullets={bulletItems}
            disabled={disabled}
            onChange={handleBulletsChange}
            onRemove={handleRemoveBullet}
            onAdd={handleAddBullet}
          />
        </>
      )}

      {/* FAQ UI */}
      {isFAQ && (
        <Box sx={{ mt: 2 }}>
          <TextField
            label="FAQ Topic"
            variant="outlined"
            size="small"
            fullWidth
            value={content.faq_topic || ""}
            onChange={(e) => handleFAQTopicChange(e.target.value)}
            disabled={disabled}
            sx={{ mb: 2 }}
          />

          <TextField
            label="FAQ Entry"
            variant="outlined"
            size="small"
            multiline
            rows={5}
            fullWidth
            value={content.faq_entry || ""}
            onChange={(e) => handleFAQEntryChange(e.target.value)}
            disabled={disabled}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Source URL (Optional)"
            variant="outlined"
            size="small"
            fullWidth
            value={content.source_url || ""}
            onChange={(e) => handleSourceUrlChange(e.target.value)}
            disabled={disabled}
            placeholder="https://example.com/source"
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Media Properties (Optional)
              </Typography>
              {mediaKeyValuePairs.some(pair => pair.key !== '' && pair.key !== 'image_url') && (
                <Typography variant="caption" color="error">
                  Only "image_url" is supported as a key
                </Typography>
              )}
            </Box>

            {mediaKeyValuePairs.map((pair, index) => (
              <Box
                key={index}
                sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}
              >
                <TextField
                  label="Key"
                  variant="outlined"
                  size="small"
                  value={pair.key}
                  onChange={(e) => handleMediaKeyChange(index, e.target.value)}
                  disabled={disabled}
                  sx={{ flex: 1 }}
                  placeholder="image_url"
                  error={pair.key !== '' && pair.key !== 'image_url'}
                />
                <TextField
                  label="Value"
                  variant="outlined"
                  size="small"
                  value={pair.value}
                  onChange={(e) =>
                    handleMediaValueChange(index, e.target.value)
                  }
                  disabled={disabled}
                  sx={{ flex: 2 }}
                  placeholder="e.g., https://example.com/image.jpg"
                />
                {!disabled && (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveMediaPair(index)}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}

            {!disabled && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddMediaPair}
                size="small"
                sx={{ mt: 1 }}
              >
                Add Media Property
              </Button>
            )}

            {mediaKeyValuePairs.length === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                No media properties defined. Click "Add Media Property" to add
                key-value pairs.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AssetContentEditor;
